import { SearchParams } from '../../service';
import { QueryValues } from '@/types/algonomy';
import AlgonomyBase from '../AlgonomyBase';
import { filterQuerypattern, mappedCustomFilters } from '../RangeFilterStrategy/types';
import { AlgonomyFrontendService } from './service';
import { Record as CookieRecord } from '@/lib/Storage/types';
import CookieStorage from '@/lib/Storage/CookieStorage';

class AlgonomyFrontend extends AlgonomyBase implements AlgonomyFrontendService {
  constructor(private readonly cookieStorage: CookieStorage) {
    super();
  }

  /* method used for creating url queries on frontend
     and then createBackendQuery reads url and create algonomy like query */
  public redirectToNextSearch(filterQuery: string, val: boolean, queryParams: QueryValues) {
    // filters='{filter1},{filter2}'
    // filter1={!tag=AmmoShape}AmmoShape:"Hollowpoint Slug"
    // filter2={!tag=AmmoShape q.op=OR}AmmoShape:("Domed/Round+nose" OR "Hollowpoint+Slug")
    // rangeFilter={!tag=product_rating q.op=OR}rating:([0+TO+1] OR [1+TO+2])
    const filterValuePattern = /\{!tag=.*?\}.*?:/; // Matches the part before the value
    const filterValue = filterQuery.replace(filterValuePattern, '').trim().slice(1, -1);
    if (val) {
      // if user selected filter
      return this.createURLQuery(filterQuery, queryParams, filterValue);
    } else {
      // if user removed filter
      return this.removeURLQuery(filterQuery, queryParams, filterValue);
    }
  }

  public async setAlgonomyCookies(cookies: CookieRecord[]): Promise<void> {
    await this.cookieStorage.putMultiple(cookies);
  }

  // this method create URL query params for frontend routing
  public createURLQuery(filterQuery: string, queryObject: QueryValues, filterValue: string): QueryValues {
    const isRange = this.isRangeFilter(filterQuery);
    const mergedObject: QueryValues = isRange
      ? this.parseRangeFilterquery(filterQuery, filterValue)
      : this.parseFilterQuery(filterQuery);
    const queryParamObject = this.getFilterValues(queryObject);
    for (const key in queryParamObject) {
      if (queryParamObject.hasOwnProperty(key)) {
        const originalValue = mergedObject[key];
        const queryParamValue = queryParamObject[key];

        this.mergeQueries(key, originalValue, queryParamValue, mergedObject);
      }
    }
    return mergedObject;
  }

  // left only true values from query params (if we had for example viewOption, left this query param)
  public validateSearchQueryParams(searchParams: SearchParams): Record<string, string | Array<string>> {
    const searchQuery = {};
    for (const key in searchParams) {
      if (searchParams[key]) {
        searchQuery[key] = searchParams[key];
      }
    }
    return searchQuery;
  }

  // removes filter params from URL query
  private removeURLQuery(filterQuery: string, queryParams: QueryValues, filterValue: string): QueryValues {
    // if user removed one of filters
    // create query params for url
    const isRange = this.isRangeFilter(filterQuery);
    const { start, ...queryParamsCopy } = queryParams;
    if (isRange) {
      this.removeRangeQuery(filterQuery, queryParamsCopy, filterValue);
      return queryParamsCopy;
    }
    const parsedFilterQuery = this.parseFilterQuery(filterQuery);
    return this.removeQuery(parsedFilterQuery, queryParamsCopy);
  }

  // if query filter param is range
  private removeRangeQuery(
    filterQuery: string,
    queryParams: Record<string, string | Array<string> | undefined>,
    filterValue: string,
  ): void {
    const parsedRangeQuery = filterQuerypattern.exec(filterQuery);
    if (parsedRangeQuery) {
      const mappedRatingQuery = mappedCustomFilters[parsedRangeQuery[1]];
      if (Array.isArray(queryParams[parsedRangeQuery[1]])) {
        if (parsedRangeQuery[1]) {
          const queryArray = queryParams[parsedRangeQuery[1]] as Array<string>;
          queryParams[parsedRangeQuery[1]] = queryArray?.filter(
            (element) => element !== mappedRatingQuery[filterValue.replace(/\\|\\/g, '')],
          );
        }
      } else {
        const propToDelete = parsedRangeQuery[1];
        delete queryParams[propToDelete];
      }
    }
  }

  private removeQuery(parsedFilterQuery: Record<string, string>, queryParamsCopy: QueryValues): QueryValues {
    for (const key in parsedFilterQuery) {
      if (queryParamsCopy.hasOwnProperty(key)) {
        if (key === 'ProductType' && parsedFilterQuery[key] === 'Accessories') {
          delete queryParamsCopy['AccessoryType'];
        }
        if (Array.isArray(queryParamsCopy[key])) {
          queryParamsCopy[key] = (queryParamsCopy[key] as Array<string>)?.filter(
            (item: string) => item !== parsedFilterQuery[key],
          );
        } else {
          const { [key]: __, ...rest } = queryParamsCopy;
          return rest;
        }
      }
    }
    return queryParamsCopy;
  }

  // utility function for handling create url for routing
  private mergeQueries(
    key: string,
    originalValue: string | Array<string> | undefined,
    queryParamValue: string | Array<string> | undefined,
    mergedObject: QueryValues,
  ): void {
    if (originalValue === undefined) {
      // Key does not exist in the original object, add it
      mergedObject[key] = queryParamValue;
    } else if (Array.isArray(originalValue) && Array.isArray(queryParamValue)) {
      // Both values are arrays, concatenate them and remove duplicates
      mergedObject[key] = Array.from(new Set([...originalValue, ...queryParamValue]));
    } else if (typeof originalValue === 'string' && Array.isArray(queryParamValue)) {
      // One value is a string and the other is an array, convert the string to an array
      mergedObject[key] = Array.from(new Set([originalValue, ...queryParamValue]));
    } else if (Array.isArray(originalValue) && typeof queryParamValue === 'string') {
      // One value is an array and the other is a string, add the string to the array
      mergedObject[key] = Array.from(new Set([...originalValue, queryParamValue]));
    } else if (typeof originalValue === 'string' && typeof queryParamValue === 'string') {
      // Both values are strings, create an array with unique values
      mergedObject[key] = Array.from(new Set([originalValue, queryParamValue]));
    }
  }
}

export default AlgonomyFrontend;
